import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { TypedMemo } from '../../../common/types';
import { AssetViewerItem } from './viewerItems';
import { viewerMessages } from './viewerMessages';
import { canStep, viewerDots } from './viewerNavigation';
import { useSwipe } from './useSwipe';
import { ViewerMedia } from './ViewerMedia';
import { ViewerWatermarkTag } from '../../watermarkUpsell/files/ViewerWatermarkTag';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the viewer; each part uses its own slice of it */
import styles from './AssetViewer.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ViewerStageProps {
  item: AssetViewerItem;
  label: string;
  songTitle: string;
  index: number;
  count: number;
  isNarrow: boolean;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  onMove: (delta: -1 | 1) => void;
  onClose: () => void;
  onVideoError: () => void;
  /** An old revision whose file is gone (design d4) shows this box in place of the file. */
  gone?: React.ReactNode;
}

/**
 * The left part of the viewer (designs a1, a2, a6): the song and the asset, the place ("3 / 14"),
 * Close, the file with the arrows, and the key hints. Below 700 px a swipe moves and the dots
 * show the place (at most 7, else the count only).
 */
const ViewerStageComponent: React.FC<ViewerStageProps> = (props) => {
  const { item, label, songTitle, index, count, isNarrow, videoRef, onMove, onClose, onVideoError, gone } = props;
  const intl = useIntl();
  const swipe = useSwipe(onMove);
  const previous = useCallback(() => onMove(-1), [onMove]);
  const next = useCallback(() => onMove(1), [onMove]);
  const dots = isNarrow ? viewerDots(index, count) : null;

  return (
    <div className={styles.stage}>
      <div className={styles.top}>
        <div className={styles.crumb}>
          <b>{songTitle}</b>
          <span aria-hidden="true">·</span>
          <span>{label}</span>
        </div>
        {count > 1 && (
          <span className={styles.count} data-testid="viewer-count">
            {intl.formatMessage(viewerMessages.count, { index: index + 1, count })}
          </span>
        )}
        <button
          type="button"
          className={clsx(styles.close, count <= 1 && 'ms-auto')}
          aria-label={intl.formatMessage(viewerMessages.close)}
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
      <div
        className={styles.media}
        onPointerDown={swipe.onPointerDown}
        onPointerUp={swipe.onPointerUp}
        onPointerCancel={swipe.onPointerCancel}
      >
        {count > 1 && (
          <button
            type="button"
            className={clsx(styles.arrow, styles.arrowLeft)}
            aria-label={intl.formatMessage(viewerMessages.previous)}
            disabled={!canStep(index, -1, count)}
            onClick={previous}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
        )}
        {gone ?? (
          <ViewerMedia key={item.key} item={item} label={label} videoRef={videoRef} onVideoError={onVideoError} />
        )}
        {/* design: trial-upsell a3. The pink tag next to the Music24 mark. */}
        {gone === undefined && (
          <ViewerWatermarkTag
            itemKey={item.key}
            hasWatermark={item.watermark}
            isVideo={item.mediaKind === 'video'}
            videoRef={videoRef}
          />
        )}
        {count > 1 && (
          <button
            type="button"
            className={clsx(styles.arrow, styles.arrowRight)}
            aria-label={intl.formatMessage(viewerMessages.next)}
            disabled={!canStep(index, 1, count)}
            onClick={next}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        )}
      </div>
      {dots !== null && (
        <div className={styles.dots} aria-hidden="true">
          {dots.map((isActive, place) => (
            // eslint-disable-next-line react/no-array-index-key -- a dot is only a place
            <i key={place} className={isActive ? styles.dotActive : undefined} />
          ))}
        </div>
      )}
      <div className={styles.keys} aria-hidden="true">
        {item.mediaKind === 'video' && (
          <span>
            <span className={styles.kbd}>Space</span>
            {intl.formatMessage(viewerMessages.keysPlay)}
          </span>
        )}
        <span>
          <span className={styles.kbd}>Esc</span>
          {intl.formatMessage(viewerMessages.keysClose)}
        </span>
        {count > 1 && (
          <span>
            <span className={styles.kbd}>←</span>
            <span className={styles.kbd}>→</span>
            {intl.formatMessage(viewerMessages.keysMove)}
          </span>
        )}
      </div>
    </div>
  );
};

export const ViewerStage = TypedMemo(ViewerStageComponent);
